import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUpRight,
  Building2,
  CircleDollarSign,
  GraduationCap,
  Search,
  Save,
  Settings2,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import api from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import { toast } from 'react-toastify';

const monthlyCtcFromEmployee = (emp) =>
  (Number(emp?.ctcBasic) || 0) +
  (Number(emp?.ctcHRA) || 0) +
  (Number(emp?.ctcSpecialAllowance) || 0) +
  (Number(emp?.ctcTravelAllowance) || 0) +
  (Number(emp?.ctcITAllowance) || 0);

const formatCurrency = (value) =>
  `₹${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const defaultSettingsForm = () => ({
  taxPageEnabled: false,
  taxPageTitle: '',
  taxPageBody: '',
  taxPageShowEarningsTable: false,
  taxPageMonthlySectionTitle: '',
  taxPageShowPf80cRow: false,
  taxPageChapterViasSectionTitle: '',
  taxPagePf80cRowLabel: '',
  taxPageShowProjectedWorksheet: false,
  taxPageWorksheetTitle: '',
  standardDeductionAnnual: '',
});

const PayrollManagement = ({ basePath = '/admin/payroll' }) => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [settingsForm, setSettingsForm] = useState(defaultSettingsForm);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const focusSearch = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    window.addEventListener('keydown', focusSearch);
    return () => window.removeEventListener('keydown', focusSearch);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/admin/employees', { params: { includeInactive: true } });
        setEmployees(res.data.employees || []);
      } catch (e) {
        console.error(e);
        toast.error('Failed to load employees');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await api.get('/admin/payroll/settings');
        const s = res.data.settings || {};
        setSettingsForm({
          taxPageEnabled: !!s.taxPageEnabled,
          taxPageTitle: s.taxPageTitle || '',
          taxPageBody: s.taxPageBody || '',
          taxPageShowEarningsTable: !!s.taxPageShowEarningsTable,
          taxPageMonthlySectionTitle: s.taxPageMonthlySectionTitle || '',
          taxPageShowPf80cRow: !!s.taxPageShowPf80cRow,
          taxPageChapterViasSectionTitle: s.taxPageChapterViasSectionTitle || '',
          taxPagePf80cRowLabel: s.taxPagePf80cRowLabel || '',
          taxPageShowProjectedWorksheet: !!s.taxPageShowProjectedWorksheet,
          taxPageWorksheetTitle: s.taxPageWorksheetTitle || '',
          standardDeductionAnnual:
            s.standardDeductionAnnual != null && s.standardDeductionAnnual !== ''
              ? String(s.standardDeductionAnnual)
              : '',
        });
      } catch (e) {
        console.error(e);
        toast.error('Failed to load payroll PDF settings');
      } finally {
        setSettingsLoading(false);
      }
    };
    loadSettings();
  }, []);

  const saveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      const payload = {
        ...settingsForm,
        standardDeductionAnnual:
          settingsForm.standardDeductionAnnual === '' ||
          settingsForm.standardDeductionAnnual == null
            ? null
            : Number(settingsForm.standardDeductionAnnual),
      };
      await api.put('/admin/payroll/settings', payload);
      toast.success('Payslip PDF settings saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const filtered = employees.filter((e) => {
    const q = search.toLowerCase();
    if (!q) return true;
    return (
      (e.name && e.name.toLowerCase().includes(q)) ||
      (e.email && e.email.toLowerCase().includes(q)) ||
      (e.employeeId && e.employeeId.toLowerCase().includes(q))
    );
  });

  const activeEmployeeList = employees.filter((employee) => employee.isActive !== false);
  const monthlyPayroll = activeEmployeeList.reduce((total, employee) => total + monthlyCtcFromEmployee(employee), 0);
  const activeEmployees = activeEmployeeList.length;
  const organizations = new Set(employees.map((employee) => employee.organization).filter(Boolean)).size;

  if (loading) {
    return <LoadingSpinner size="lg" className="py-16" />;
  }

  return (
    <div className="space-y-6 pb-8">
      <section className="relative isolate overflow-hidden rounded-3xl bg-gradient-to-br from-sky-400 via-cyan-500 to-blue-600 px-6 py-7 text-white shadow-xl shadow-sky-900/15 sm:px-8">
        <div className="absolute -right-12 -top-24 -z-10 h-72 w-72 rounded-full bg-white/25 blur-3xl" />
        <div className="absolute -bottom-32 left-1/3 -z-10 h-64 w-64 rounded-full bg-sky-200/25 blur-3xl" />
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/15 px-3 py-1.5 text-xs font-semibold tracking-wide text-sky-50 shadow-sm shadow-sky-900/10">
              <Wallet className="h-3.5 w-3.5 text-white" />
              Payroll workspace
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-white drop-shadow-sm sm:text-4xl">Payroll, made clearer.</h1>
            <p className="mt-3 max-w-xl text-sm leading-6 text-sky-50/95 sm:text-base">
              Manage monthly compensation, configure payslip compliance, and open employee payroll details from one place.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-3 rounded-2xl border border-white/25 bg-white/15 px-4 py-3 shadow-lg shadow-sky-900/10 backdrop-blur-sm">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 text-white">
              <Users className="h-5 w-5" />
            </span>
            <div>
              <p className="text-xs text-sky-50/80">Active employees</p>
              <p className="text-xl font-bold text-white">{activeEmployees}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm shadow-slate-200/60">
          <div className="flex items-start justify-between">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
              <Users className="h-5 w-5" />
            </span>
            <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">Live</span>
          </div>
          <p className="mt-5 text-sm font-medium text-slate-500">Employees on payroll</p>
          <p className="mt-1 text-2xl font-bold tracking-tight text-slate-900">{activeEmployees}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm shadow-slate-200/60">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-50 text-violet-600">
            <CircleDollarSign className="h-5 w-5" />
          </span>
          <p className="mt-5 text-sm font-medium text-slate-500">Monthly payroll volume</p>
          <p className="mt-1 text-2xl font-bold tracking-tight text-slate-900">{formatCurrency(monthlyPayroll)}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm shadow-slate-200/60">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
            <Building2 className="h-5 w-5" />
          </span>
          <p className="mt-5 text-sm font-medium text-slate-500">Organizations covered</p>
          <p className="mt-1 text-2xl font-bold tracking-tight text-slate-900">{organizations}</p>
        </div>
      </section>

      <details className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm shadow-slate-200/60">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-5 py-4 text-slate-900 transition hover:bg-slate-50 sm:px-6">
          <span className="flex min-w-0 items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
              <Settings2 className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <span className="block text-base font-bold sm:text-lg">Payslip PDF settings</span>
              <span className="mt-0.5 block truncate text-xs font-normal text-slate-500 sm:text-sm">Optional company tax and compliance page</span>
            </span>
          </span>
          <span className="hidden shrink-0 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600 sm:block">Configure</span>
        </summary>
        <div className="border-t border-slate-100 px-5 pb-5 pt-0 sm:px-6">
          {settingsLoading ? (
            <div className="py-8 flex justify-center">
              <LoadingSpinner />
            </div>
          ) : (
            <form onSubmit={saveSettings} className="space-y-5 mt-4">
              <p className="text-sm text-gray-600">
                When enabled, the text you enter below is stored in the database and copied onto each payslip
                when HR generates it. If you leave this off, payslips are a single page. Optional tables use
                <strong> real figures</strong> from that payslip (earnings, PF, TDS); the annual standard
                deduction is whatever amount <strong>you</strong> enter for your policy.
              </p>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settingsForm.taxPageEnabled}
                  onChange={(e) =>
                    setSettingsForm({ ...settingsForm, taxPageEnabled: e.target.checked })
                  }
                  className="rounded border-gray-300"
                />
                <span className="text-sm font-medium text-gray-800">Include second page on payslip PDFs</span>
              </label>

              <div>
                <label className="block text-sm font-medium text-gray-700">Page title</label>
                <input
                  type="text"
                  className="mt-1 w-full max-w-xl border border-gray-300 rounded-md px-3 py-2 text-sm"
                  value={settingsForm.taxPageTitle}
                  onChange={(e) => setSettingsForm({ ...settingsForm, taxPageTitle: e.target.value })}
                  placeholder="e.g. Tax and TDS information"
                  disabled={!settingsForm.taxPageEnabled}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Page body</label>
                <p className="text-xs text-gray-500 mt-0.5">Use line breaks for paragraphs.</p>
                <textarea
                  rows={6}
                  className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono"
                  value={settingsForm.taxPageBody}
                  onChange={(e) => setSettingsForm({ ...settingsForm, taxPageBody: e.target.value })}
                  placeholder="Enter your company-approved compliance text."
                  disabled={!settingsForm.taxPageEnabled}
                />
              </div>

              <div className="border border-gray-100 rounded-lg p-4 space-y-3 bg-slate-50/50">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settingsForm.taxPageShowEarningsTable}
                    onChange={(e) =>
                      setSettingsForm({
                        ...settingsForm,
                        taxPageShowEarningsTable: e.target.checked,
                      })
                    }
                    disabled={!settingsForm.taxPageEnabled}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm font-medium text-gray-800">
                    Include earnings table (amounts from this payslip)
                  </span>
                </label>
                <input
                  type="text"
                  className="w-full max-w-md border border-gray-300 rounded-md px-3 py-2 text-sm"
                  placeholder="Section heading for that table"
                  value={settingsForm.taxPageMonthlySectionTitle}
                  onChange={(e) =>
                    setSettingsForm({ ...settingsForm, taxPageMonthlySectionTitle: e.target.value })
                  }
                  disabled={!settingsForm.taxPageEnabled || !settingsForm.taxPageShowEarningsTable}
                />
              </div>

              <div className="border border-gray-100 rounded-lg p-4 space-y-3 bg-slate-50/50">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settingsForm.taxPageShowPf80cRow}
                    onChange={(e) =>
                      setSettingsForm({ ...settingsForm, taxPageShowPf80cRow: e.target.checked })
                    }
                    disabled={!settingsForm.taxPageEnabled}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm font-medium text-gray-800">
                    Include PF row (employee PF from this payslip)
                  </span>
                </label>
                <input
                  type="text"
                  className="w-full max-w-md border border-gray-300 rounded-md px-3 py-2 text-sm"
                  placeholder="Section title (e.g. Chapter VI-A)"
                  value={settingsForm.taxPageChapterViasSectionTitle}
                  onChange={(e) =>
                    setSettingsForm({
                      ...settingsForm,
                      taxPageChapterViasSectionTitle: e.target.value,
                    })
                  }
                  disabled={!settingsForm.taxPageEnabled || !settingsForm.taxPageShowPf80cRow}
                />
                <input
                  type="text"
                  className="w-full max-w-md border border-gray-300 rounded-md px-3 py-2 text-sm"
                  placeholder="Row label (e.g. PF employee contribution)"
                  value={settingsForm.taxPagePf80cRowLabel}
                  onChange={(e) =>
                    setSettingsForm({ ...settingsForm, taxPagePf80cRowLabel: e.target.value })
                  }
                  disabled={!settingsForm.taxPageEnabled || !settingsForm.taxPageShowPf80cRow}
                />
              </div>

              <div className="border border-gray-100 rounded-lg p-4 space-y-3 bg-slate-50/50">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settingsForm.taxPageShowProjectedWorksheet}
                    onChange={(e) =>
                      setSettingsForm({
                        ...settingsForm,
                        taxPageShowProjectedWorksheet: e.target.checked,
                      })
                    }
                    disabled={!settingsForm.taxPageEnabled}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm font-medium text-gray-800">
                    Include projected annual worksheet (gross×12, your standard deduction, PF×12, taxable, TDS)
                  </span>
                </label>
                <input
                  type="text"
                  className="w-full max-w-md border border-gray-300 rounded-md px-3 py-2 text-sm"
                  placeholder="Worksheet section title"
                  value={settingsForm.taxPageWorksheetTitle}
                  onChange={(e) =>
                    setSettingsForm({ ...settingsForm, taxPageWorksheetTitle: e.target.value })
                  }
                  disabled={!settingsForm.taxPageEnabled || !settingsForm.taxPageShowProjectedWorksheet}
                />
                <div>
                  <label className="text-sm text-gray-700">Annual standard deduction (₹)</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    className="mt-1 w-40 border border-gray-300 rounded-md px-3 py-2 text-sm"
                    value={settingsForm.standardDeductionAnnual}
                    onChange={(e) =>
                      setSettingsForm({ ...settingsForm, standardDeductionAnnual: e.target.value })
                    }
                    disabled={!settingsForm.taxPageEnabled || !settingsForm.taxPageShowProjectedWorksheet}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={savingSettings}
                className="inline-flex items-center px-4 py-2 bg-gray-900 text-white rounded-md text-sm hover:bg-gray-800 disabled:opacity-50"
              >
                <Save className="h-4 w-4 mr-2" />
                {savingSettings ? 'Saving…' : 'Save company payslip settings'}
              </button>
            </form>
          )}
        </div>
      </details>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm shadow-slate-200/60 sm:p-5">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-sm font-bold text-slate-900">Find an employee</p>
            <p className="mt-0.5 text-xs text-slate-500">Search by name, email, or employee ID</p>
          </div>
          <kbd className="hidden rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] font-semibold text-slate-500 sm:inline-block">Ctrl K</kbd>
        </div>
        <div className="group flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/70 px-3.5 py-3 transition focus-within:border-primary-400 focus-within:bg-white focus-within:ring-4 focus-within:ring-primary-50">
          <Search className="h-5 w-5 shrink-0 text-slate-400 transition group-focus-within:text-primary-600" />
          <input
            ref={searchInputRef}
            type="search"
            placeholder="Search by name, email, or employee ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search employees"
            className="min-w-0 flex-1 border-0 bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:ring-0"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              aria-label="Clear employee search"
              className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-200 hover:text-slate-700"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </section>

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm shadow-slate-200/60">
        <div className="flex flex-col gap-3 border-b border-slate-100 px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
              <Users className="h-5 w-5" />
            </span>
            <div>
              <h2 className="text-lg font-bold text-slate-900">All employees</h2>
              <p className="text-xs text-slate-500">Open a profile to run payroll and generate payslips</p>
            </div>
          </div>
          <span className="self-start rounded-full bg-primary-50 px-3 py-1.5 text-xs font-semibold text-primary-700 sm:self-auto">
            {filtered.length} of {employees.length} shown
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50/80">
              <tr>
                <th className="px-5 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500 sm:px-6">
                  Employee
                </th>
                <th className="px-5 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500 sm:px-6">
                  Organization
                </th>
                <th className="px-5 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500 sm:px-6">
                  Monthly CTC
                </th>
                <th className="px-5 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500 sm:px-6">
                  Account
                </th>
                <th className="px-5 py-3.5 text-right text-[11px] font-bold uppercase tracking-wider text-slate-500 sm:px-6">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {filtered.map((emp) => (
                <tr key={emp._id} className={`transition hover:bg-slate-50 ${emp.isActive === false ? 'opacity-60' : ''}`}>
                  <td className="whitespace-nowrap px-5 py-4 sm:px-6">
                    <div className="text-sm font-bold text-slate-900">{emp.name}</div>
                    <div className="text-sm text-gray-500">{emp.employeeId || '—'}</div>
                    {emp.isIntern && (
                      <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-700">
                        <GraduationCap className="h-3 w-3" /> Intern · professional tax waived
                      </span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-5 py-4 text-sm text-slate-600 sm:px-6">
                    {emp.organization || '—'}
                  </td>
                  <td className="whitespace-nowrap px-5 py-4 text-sm font-semibold text-slate-900 sm:px-6">
                    ₹{monthlyCtcFromEmployee(emp).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </td>
                  <td className="whitespace-nowrap px-5 py-4 sm:px-6">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${emp.isActive ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                      {emp.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-5 py-4 text-right text-sm sm:px-6">
                    <Link
                      to={`${basePath}/${emp._id}`}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3.5 py-2 font-semibold text-white shadow-sm shadow-primary-600/20 transition hover:-translate-y-0.5 hover:bg-primary-700 hover:shadow-md"
                    >
                      Open payroll
                      <ArrowUpRight className="h-4 w-4" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <p className="text-center text-gray-500 py-10">No employees match your search.</p>
        )}
      </section>
    </div>
  );
};

export default PayrollManagement;
